/**
 * Generate a memorable password in what3words style (word.word.word),
 * using a local English word list (not the official what3words API).
 *
 * Always includes at least one capital letter, one digit, and one UK-keyboard
 * special character; overall length is always well above 8.
 */

/** Specials available without obscure Alt codes on a UK QWERTY layout.
 *  Dot is omitted so it stays the what3words-style word separator only. */
const SPECIALS = '!@#$%^&*()-_=+[]{}:;,?/~#£'
const DIGITS = '23456789'

/**
 * Short, typeable English words (a–z only). Enough for useful entropy when
 * three are chosen at random; avoids lookalike letters where practical.
 */
const WORDS = [
  'able', 'acid', 'acre', 'actor', 'adapt', 'admit', 'adopt', 'adult', 'after',
  'agent', 'agree', 'ahead', 'alarm', 'album', 'alert', 'alien', 'align', 'alike',
  'alive', 'allow', 'alloy', 'alone', 'along', 'alpha', 'altar', 'amber', 'amend',
  'angel', 'anger', 'angle', 'ankle', 'apart', 'apple', 'apply', 'apron', 'arena',
  'argue', 'arise', 'armed', 'arrow', 'asian', 'asset', 'atlas', 'audio', 'audit',
  'avoid', 'await', 'awake', 'award', 'aware', 'bacon', 'badge', 'baker', 'balloon',
  'banjo', 'barge', 'basin', 'batch', 'beach', 'beard', 'beast', 'begin', 'beige',
  'bench', 'berry', 'bible', 'bike', 'bind', 'birch', 'bird', 'blade', 'blame',
  'blank', 'blast', 'blaze', 'bleak', 'blend', 'bless', 'blind', 'bliss', 'block',
  'blood', 'bloom', 'blown', 'blues', 'blunt', 'board', 'boast', 'bonus', 'boost',
  'booth', 'borne', 'boss', 'bound', 'bowel', 'boxer', 'brain', 'brand', 'brass',
  'brave', 'bread', 'break', 'breed', 'brick', 'bride', 'brief', 'bring', 'brink',
  'broad', 'broke', 'brook', 'brown', 'brush', 'buddy', 'build', 'built', 'bunch',
  'burst', 'buyer', 'cabin', 'cable', 'cadet', 'camel', 'canal', 'candy', 'canoe',
  'canon', 'cargo', 'carol', 'carry', 'carve', 'catch', 'cause', 'cedar', 'chain',
  'chair', 'chalk', 'champ', 'chart', 'chase', 'cheap', 'check', 'cheek', 'cheer',
  'chess', 'chest', 'chief', 'child', 'chill', 'china', 'choir', 'chord', 'chunk',
  'cider', 'cigar', 'civic', 'civil', 'claim', 'clamp', 'clash', 'clasp', 'class',
  'clean', 'clear', 'clerk', 'click', 'cliff', 'climb', 'cling', 'cloak', 'clock',
  'clone', 'close', 'cloth', 'cloud', 'clown', 'coast', 'cocoa', 'colon', 'color',
  'comet', 'comic', 'comma', 'coral', 'couch', 'cough', 'count', 'court', 'cover',
  'craft', 'crane', 'crash', 'crate', 'crave', 'crawl', 'crazy', 'cream', 'creek',
  'crest', 'crisp', 'cross', 'crowd', 'crown', 'crude', 'cruel', 'crush', 'curve',
  'cycle', 'dairy', 'daisy', 'dance', 'dated', 'dealt', 'death', 'debut', 'decay',
  'decor', 'delay', 'delta', 'dense', 'depot', 'depth', 'derby', 'digit', 'diner',
  'dirty', 'disco', 'ditch', 'dizzy', 'dodge', 'donor', 'doubt', 'dough', 'dozen',
  'draft', 'drain', 'drake', 'drama', 'drank', 'drawn', 'dream', 'dress', 'dried',
  'drift', 'drill', 'drink', 'drive', 'drone', 'drown', 'drunk', 'dryer', 'eager',
  'eagle', 'early', 'earth', 'easel', 'eaten', 'ebony', 'eclat', 'eden', 'edge',
  'elbow', 'elder', 'elect', 'elite', 'ember', 'empty', 'enact', 'ended', 'enemy',
  'enjoy', 'enter', 'entry', 'envoy', 'equal', 'error', 'essay', 'ethic', 'event',
  'every', 'exact', 'excel', 'exile', 'exist', 'extra', 'fable', 'facet', 'faith',
  'false', 'fancy', 'farce', 'fault', 'feast', 'fence', 'ferry', 'fever', 'fibre',
  'field', 'fiery', 'fifth', 'fifty', 'fight', 'final', 'finch', 'fired', 'first',
  'fish', 'fixed', 'flair', 'flame', 'flare', 'flash', 'flask', 'fleet', 'flesh',
  'flick', 'fling', 'flint', 'flock', 'flood', 'floor', 'flora', 'flour', 'flown',
  'fluid', 'flute', 'focal', 'focus', 'foggy', 'force', 'forge', 'forte', 'forth',
  'forty', 'forum', 'found', 'frame', 'frank', 'fraud', 'freak', 'fresh', 'friar',
  'fried', 'front', 'frost', 'fruit', 'fudge', 'fully', 'fungi', 'funny', 'furry',
  'fused', 'fuzzy', 'gable', 'gamma', 'gauge', 'ghost', 'giant', 'given', 'glass',
  'glaze', 'gleam', 'glide', 'globe', 'gloom', 'glory', 'glove', 'going', 'goose',
  'grace', 'grade', 'grain', 'grand', 'grant', 'grape', 'graph', 'grasp', 'grass',
  'grave', 'great', 'greed', 'green', 'greet', 'grief', 'grill', 'grind', 'groom',
  'groan', 'group', 'grove', 'grown', 'guard', 'guess', 'guest', 'guide', 'guild',
  'guilt', 'habit', 'hairy', 'happy', 'hardy', 'harsh', 'haste', 'hatch', 'haven',
  'havoc', 'hazel', 'head', 'heard', 'heart', 'heath', 'heave', 'heavy', 'hedge',
  'hefty', 'helix', 'hello', 'hence', 'heron', 'hinge', 'hippo', 'hobby', 'honey',
  'honor', 'horse', 'hotel', 'hound', 'house', 'hover', 'human', 'humid', 'humor',
  'hurry', 'husky', 'ideal', 'image', 'imply', 'inbox', 'incur', 'index', 'indie',
  'inner', 'input', 'intro', 'irony', 'issue', 'ivory', 'jelly', 'jewel', 'joint',
  'jolly', 'judge', 'juice', 'jumbo', 'jumpy', 'karma', 'kayak', 'kebab', 'khaki',
  'kiosk', 'knack', 'kneel', 'knife', 'knock', 'known', 'label', 'labor', 'laden',
  'lance', 'large', 'laser', 'later', 'laugh', 'layer', 'leach', 'learn', 'lease',
  'least', 'leave', 'legal', 'lemon', 'level', 'lever', 'light', 'lilac', 'limit',
  'linen', 'liner', 'liver', 'lobby', 'local', 'logic', 'loose', 'lorry', 'lotus',
  'loyal', 'lucky', 'lunar', 'lunch', 'lying', 'lyric', 'magic', 'magma', 'maize',
  'major', 'maker', 'mango', 'manor', 'maple', 'march', 'marsh', 'match', 'maybe',
  'mayor', 'meant', 'medal', 'media', 'mercy', 'merge', 'merit', 'merry', 'metal',
  'meter', 'metro', 'micro', 'midst', 'might', 'minor', 'minus', 'mirth', 'mixed',
  'model', 'modem', 'moist', 'molar', 'money', 'month', 'moral', 'motor', 'motto',
  'mould', 'mount', 'mouse', 'mouth', 'movie', 'muddy', 'mummy', 'music', 'naive',
  'nasal', 'nasty', 'naval', 'nerve', 'never', 'newer', 'newly', 'nexus', 'niche',
  'night', 'noble', 'noise', 'north', 'notch', 'noted', 'novel', 'nurse', 'nylon',
  'oasis', 'ocean', 'octet', 'offer', 'often', 'olive', 'omega', 'onion', 'onset',
  'opera', 'orbit', 'order', 'organ', 'other', 'otter', 'ought', 'ounce', 'outer',
  'owing', 'owner', 'oxide', 'ozone', 'paint', 'panel', 'panic', 'paper', 'parch',
  'party', 'pasta', 'patch', 'patio', 'pause', 'peace', 'peach', 'pearl', 'pedal',
  'penal', 'penny', 'perch', 'peril', 'petal', 'phase', 'phone', 'photo', 'piano',
  'piece', 'pilot', 'pinch', 'pitch', 'pivot', 'pixel', 'pizza', 'place', 'plain',
  'plane', 'plant', 'plate', 'plaza', 'plead', 'pluck', 'plumb', 'plume', 'plush',
  'point', 'polar', 'porch', 'pound', 'power', 'press', 'price', 'pride', 'prime',
  'print', 'prior', 'prism', 'prize', 'probe', 'prone', 'proof', 'prose', 'proud',
  'prove', 'proxy', 'prune', 'pulse', 'punch', 'pupil', 'puppy', 'purse', 'quack',
  'quark', 'quart', 'queen', 'query', 'quest', 'queue', 'quick', 'quiet', 'quilt',
  'quota', 'quote', 'radar', 'radio', 'raven', 'razor', 'reach', 'react', 'ready',
  'realm', 'rebel', 'rebus', 'recap', 'recon', 'refer', 'reign', 'relax', 'relay',
  'relic', 'remit', 'renew', 'repay', 'reply', 'reset', 'resin', 'retro', 'retry',
  'reuse', 'rhino', 'rhyme', 'rider', 'ridge', 'rifle', 'right', 'rigid', 'rigor',
  'rinse', 'risky', 'rival', 'river', 'roast', 'robot', 'rocky', 'rogue', 'roman',
  'rotor', 'rouge', 'rough', 'round', 'route', 'rover', 'royal', 'rugby', 'ruler',
  'rumor', 'rural', 'rusty', 'sadly', 'safer', 'saint', 'salad', 'salon', 'salsa',
  'salty', 'salve', 'sandy', 'satin', 'sauce', 'sauna', 'saved', 'scale', 'scalp',
  'scant', 'scare', 'scarf', 'scene', 'scent', 'scope', 'score', 'scout', 'scrap',
  'screw', 'scrub', 'seize', 'sense', 'sepia', 'serve', 'setup', 'seven', 'shade',
  'shaft', 'shake', 'shall', 'shame', 'shape', 'share', 'shark', 'sharp', 'shave',
  'shear', 'sheep', 'sheer', 'sheet', 'shelf', 'shell', 'shift', 'shine', 'shire',
  'shirt', 'shock', 'shore', 'short', 'shout', 'shown', 'sided', 'siege', 'sight',
  'sigma', 'silky', 'silly', 'since', 'siren', 'sixth', 'sixty', 'skill', 'skirt',
  'skull', 'slack', 'slang', 'slate', 'slave', 'sleek', 'sleep', 'sleet', 'slice',
  'slide', 'slime', 'sling', 'slope', 'sloth', 'slump', 'small', 'smart', 'smash',
  'smear', 'smell', 'smile', 'smirk', 'smoke', 'snack', 'snail', 'snake', 'snare',
  'sneak', 'sniff', 'sober', 'solar', 'solid', 'solve', 'sonar', 'sonic', 'sorry',
  'sound', 'south', 'space', 'spade', 'spare', 'spark', 'speak', 'spear', 'speed',
  'spell', 'spend', 'spice', 'spicy', 'spike', 'spill', 'spine', 'spite', 'split',
  'spoil', 'spoke', 'spoon', 'sport', 'spot', 'spray', 'squad', 'stack', 'staff',
  'stage', 'stain', 'stair', 'stake', 'stale', 'stalk', 'stall', 'stamp', 'stand',
  'stare', 'stark', 'start', 'state', 'steak', 'steal', 'steam', 'steel', 'steep',
  'steer', 'stem', 'stick', 'stiff', 'still', 'stock', 'stone', 'stool', 'store',
  'storm', 'story', 'stove', 'strap', 'straw', 'stray', 'strip', 'stuck', 'study',
  'stuff', 'stunt', 'style', 'sugar', 'suite', 'sunny', 'super', 'surge', 'swamp',
  'swarm', 'swear', 'sweat', 'sweep', 'sweet', 'swell', 'swept', 'swift', 'swing',
  'sword', 'sworn', 'syrup', 'table', 'tacit', 'taken', 'tango', 'taper', 'taste',
  'tasty', 'teach', 'tease', 'teeth', 'tempo', 'tenant', 'tenor', 'tense', 'tenth',
  'tepid', 'terra', 'thank', 'theft', 'their', 'theme', 'there', 'these', 'thick',
  'thief', 'thigh', 'thing', 'think', 'third', 'thorn', 'those', 'three', 'threw',
  'throw', 'thumb', 'tidal', 'tiger', 'tight', 'timer', 'tired', 'title', 'toast',
  'today', 'token', 'toner', 'tonic', 'tooth', 'topic', 'torch', 'torso', 'total',
  'touch', 'tough', 'towel', 'tower', 'toxic', 'trace', 'track', 'trade', 'trail',
  'train', 'trait', 'tram', 'trap', 'trash', 'trawl', 'tray', 'treat', 'tree',
  'trend', 'trial', 'tribe', 'trick', 'tried', 'trip', 'truce', 'truck', 'truly',
  'trump', 'trunk', 'trust', 'truth', 'tulip', 'tuner', 'turbo', 'tutor', 'tweed',
  'twice', 'twist', 'tying', 'ultra', 'uncle', 'under', 'undue', 'union', 'unite',
  'unity', 'until', 'upper', 'upset', 'urban', 'urged', 'urine', 'usage', 'usher',
  'using', 'usual', 'utter', 'vague', 'valet', 'valid', 'value', 'valve', 'vapor',
  'vault', 'vegan', 'venom', 'venue', 'verge', 'verse', 'video', 'vigil', 'vigor',
  'villa', 'vinyl', 'viola', 'viper', 'viral', 'virus', 'visit', 'visor', 'vista',
  'vital', 'vivid', 'vocal', 'vodka', 'vogue', 'voice', 'voted', 'vowel', 'wager',
  'wagon', 'waist', 'waive', 'waltz', 'waste', 'watch', 'water', 'wave', 'waxen',
  'weary', 'weave', 'wedge', 'weigh', 'weird', 'welsh', 'whale', 'wheat', 'wheel',
  'where', 'which', 'while', 'whine', 'whirl', 'white', 'whole', 'whose', 'widen',
  'wider', 'widow', 'width', 'wield', 'wills', 'winds', 'wines', 'wings', 'wiped',
  'wired', 'wispy', 'witch', 'witty', 'woken', 'woman', 'women', 'world', 'worry',
  'worse', 'worst', 'worth', 'would', 'wound', 'woven', 'wrath', 'wreck', 'wrest',
  'wring', 'wrist', 'write', 'wrong', 'wrote', 'yacht', 'yearn', 'yeast', 'yield',
  'young', 'youth', 'zebra', 'zesty', 'zonal',
]

function pickIndex(mod, randomByte) {
  return randomByte % mod
}

function pickChar(alphabet, randomByte) {
  return alphabet[randomByte % alphabet.length]
}

function capitalize(word) {
  return word.charAt(0).toUpperCase() + word.slice(1)
}

/** Three distinct random words joined with dots, plus digit + UK special. */
export function generatePassword() {
  const bytes = new Uint8Array(8)
  crypto.getRandomValues(bytes)

  const indices = []
  let offset = 0
  while (indices.length < 3) {
    const idx = pickIndex(WORDS.length, bytes[offset % bytes.length])
    offset += 1
    if (offset >= bytes.length) {
      crypto.getRandomValues(bytes)
      offset = 0
    }
    if (!indices.includes(idx)) indices.push(idx)
  }

  // Extra entropy if we reused the buffer while avoiding duplicates
  crypto.getRandomValues(bytes)

  const words = indices.map((i) => WORDS[i])
  // Capitalise a random word so the password always has an uppercase letter
  const capAt = pickIndex(3, bytes[0])
  words[capAt] = capitalize(words[capAt])

  const digit = pickChar(DIGITS, bytes[1])
  const special = pickChar(SPECIALS, bytes[2])

  // what3words-style body, then digit + special to meet the strength rules
  return `${words.join('.')}${digit}${special}`
}
